import { useState } from 'react';
import type { ProjectFilesMeta } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { SideDialog } from '../team/SideDialog.js';
import { uploadLimitText } from '../references/format.js';
import { UploadRow } from '../upload/UploadRow.js';
import { uploadSummaryText, type QueuedUpload, type RejectedFile, type UploadSummary } from '../upload/useUploadQueue.js';

export interface FileUploads {
  readonly items: readonly QueuedUpload<true>[];
  readonly rejected: readonly RejectedFile[];
  readonly summary: UploadSummary;
  readonly retryFailed: () => void;
  readonly remove: (key: string) => void;
}

/**
 * THE UPLOAD SHEET — where a batch of files is followed while it sends: every file
 * with its own byte progress, what was refused before sending and why, what failed
 * and the server's reason, and Retry for only the failures, under the keys they were
 * first sent with, so a file that did arrive is never stored twice.
 *
 * Files start the moment they are chosen or dropped (nothing to fill in first); the
 * sheet can be closed while they send — the page keeps a one-line banner — and more
 * files can be added from it. Full width on a phone (`SideDialog`).
 */
export function UploadSheet({
  uploads,
  meta,
  onChoose,
  onDrop,
  onClose,
}: {
  uploads: FileUploads;
  meta: ProjectFilesMeta | undefined;
  onChoose: () => void;
  onDrop: (files: File[]) => void;
  onClose: () => void;
}) {
  const [over, setOver] = useState(false);
  const { summary } = uploads;
  const finished = summary.total > 0 && summary.active === 0 && summary.queued === 0;

  return (
    <SideDialog title="Upload files" testId="files-upload-sheet" size="lg" onClose={onClose}>
      <div className="ref-add-form" data-testid="files-upload-form">
        <div
          className="ref-drop ref-drop-large"
          data-over={over ? '1' : undefined}
          data-testid="files-upload-drop"
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => {
            setOver(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setOver(false);
            const files = Array.from(event.dataTransfer.files);
            if (files.length > 0) onDrop(files);
          }}
        >
          <Icon name="cloudUpload" size={22} />
          <span className="ref-drop-text">
            <span>{summary.total === 0 ? 'Drop files here, or' : 'Drop more files here, or'}</span>
            {meta ? (
              <span className="ref-drop-limit" data-testid="files-upload-limit">
                {uploadLimitText(meta.upload)}
              </span>
            ) : null}
          </span>
          <button type="button" className="ctl" data-autofocus data-testid="files-upload-choose" onClick={onChoose}>
            {summary.total === 0 ? 'Choose files' : 'Add more files'}
          </button>
        </div>

        <p className="ref-quiet" data-testid="files-upload-private">
          Uploaded files are kept privately for this production. They cannot be opened or downloaded in the workspace
          until file checking is available.
        </p>

        {uploads.rejected.length > 0 ? (
          <ul className="ref-upload-list" aria-label="Files that will not be uploaded" data-testid="files-upload-rejected">
            {uploads.rejected.map((r) => (
              <li key={r.key} className="ref-upload-item" data-state="rejected" data-testid={`files-upload-rejected-${r.name}`}>
                <span className="ref-upload-name">{r.name}</span>
                <span className="ref-upload-status" data-tone="danger">
                  {r.reason}
                </span>
                <button
                  type="button"
                  className="ref-chip-x"
                  aria-label={`Dismiss ${r.name}`}
                  data-testid={`files-upload-dismiss-${r.name}`}
                  onClick={() => {
                    uploads.remove(r.key);
                  }}
                >
                  <Icon name="close" size={12} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {uploads.items.length > 0 ? (
          <ul className="ref-upload-list" aria-label="Files" data-testid="files-upload-list">
            {uploads.items.map((item) => (
              <UploadRow
                key={item.key}
                item={item}
                removable={item.state === 'queued'}
                doneLabel="Uploaded"
                testIdPrefix="files-upload"
                onRemove={() => {
                  uploads.remove(item.key);
                }}
              />
            ))}
          </ul>
        ) : null}

        {summary.total > 0 ? (
          <p className="ref-upload-summary" role="status" aria-live="polite" data-testid="files-upload-summary">
            {uploadSummaryText(summary, 'uploaded')}
          </p>
        ) : null}

        <div className="ref-add-footer">
          <div className="ref-add-actions">
            {summary.failed > 0 && summary.active === 0 && summary.queued === 0 ? (
              <button type="button" className="ctl" data-testid="files-upload-retry" onClick={uploads.retryFailed}>
                {`Retry failed (${String(summary.failed)})`}
              </button>
            ) : null}
            <button type="button" className="ctl" data-testid="files-upload-close" onClick={onClose}>
              {finished || summary.total === 0 ? 'Done' : 'Hide — uploads continue'}
            </button>
          </div>
        </div>
      </div>
    </SideDialog>
  );
}
